import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle, Loader2 } from 'lucide-react'
import { Button } from './Button'
import { LocalizedLink } from '@/components/LocalizedLink'
import { apiUrl } from '@/lib/api'
import { cn } from '@/lib/utils'

export function ContactForm() {
  const { t } = useTranslation(['contacts', 'common'])

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [city, setCity] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState('')
  const [privacy, setPrivacy] = useState(false)

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const validate = () => {
    const nextErrors: Record<string, string> = {}
    if (!firstName.trim()) {
      nextErrors.firstName = t('contacts:form.validation.firstNameRequired')
    }
    if (!lastName.trim()) {
      nextErrors.lastName = t('contacts:form.validation.lastNameRequired')
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextErrors.email = t('contacts:form.validation.emailInvalid')
    }
    if (!message.trim()) {
      nextErrors.message = t('contacts:form.validation.messageRequired')
    }
    if (!privacy) {
      nextErrors.privacy = t('contacts:form.validation.privacyRequired')
    }
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)
    if (!validate()) return

    setSubmitting(true)
    try {
      const res = await fetch(apiUrl('/api/contact'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          city: city.trim() || undefined,
          email: email.trim(),
          phone: phone.trim() || undefined,
          message: message.trim(),
          privacyConsent: true,
        }),
      })
      if (!res.ok) throw new Error('Contact failed')
      setSuccess(true)
    } catch {
      setSubmitError(t('contacts:form.error.message'))
    } finally {
      setSubmitting(false)
    }
  }

  const reset = () => {
    setFirstName('')
    setLastName('')
    setCity('')
    setEmail('')
    setPhone('')
    setMessage('')
    setPrivacy(false)
    setErrors({})
    setSubmitError(null)
    setSuccess(false)
  }

  const inputBase =
    'w-full rounded-lg border bg-ink-deep/60 px-4 py-3 text-white placeholder:text-white/30 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors'
  const inputError = 'border-red-400/60 focus:border-red-400 focus:ring-red-400'
  const inputNormal = 'border-white/10 focus:border-primary'

  if (success) {
    return (
      <div className="rounded-2xl border border-primary/20 bg-ink p-8 md:p-12 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-6">
          <CheckCircle className="w-8 h-8" />
        </div>
        <h3 className="font-display text-2xl md:text-3xl text-white">
          {t('contacts:form.success.title')}
        </h3>
        <p className="mt-3 text-white/70 max-w-md mx-auto">
          {t('contacts:form.success.message')}
        </p>
        <div className="mt-8">
          <Button type="button" onClick={reset}>
            {t('common:buttons.send')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-2xl border border-white/10 bg-ink p-6 md:p-8 lg:p-10">
      <h2 className="font-display text-2xl md:text-3xl text-white mb-6">
        {t('contacts:form.title')}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* First name */}
        <div>
          <label htmlFor="contact-first-name" className="block text-xs font-medium uppercase tracking-wide text-white/60 mb-1.5">
            {t('contacts:form.firstName')} *
          </label>
          <input
            id="contact-first-name"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder={t('contacts:form.firstNamePlaceholder')}
            className={cn(inputBase, errors.firstName ? inputError : inputNormal)}
          />
          {errors.firstName && <p className="mt-1.5 text-sm text-red-400">{errors.firstName}</p>}
        </div>

        {/* Last name */}
        <div>
          <label htmlFor="contact-last-name" className="block text-xs font-medium uppercase tracking-wide text-white/60 mb-1.5">
            {t('contacts:form.lastName')} *
          </label>
          <input
            id="contact-last-name"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder={t('contacts:form.lastNamePlaceholder')}
            className={cn(inputBase, errors.lastName ? inputError : inputNormal)}
          />
          {errors.lastName && <p className="mt-1.5 text-sm text-red-400">{errors.lastName}</p>}
        </div>

        {/* City */}
        <div>
          <label htmlFor="contact-city" className="block text-xs font-medium uppercase tracking-wide text-white/60 mb-1.5">
            {t('contacts:form.city')}
          </label>
          <input
            id="contact-city"
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder={t('contacts:form.cityPlaceholder')}
            className={cn(inputBase, inputNormal)}
          />
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="contact-phone" className="block text-xs font-medium uppercase tracking-wide text-white/60 mb-1.5">
            {t('contacts:form.phone')}
          </label>
          <input
            id="contact-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t('contacts:form.phonePlaceholder')}
            className={cn(inputBase, inputNormal)}
          />
        </div>

        {/* Email */}
        <div className="md:col-span-2">
          <label htmlFor="contact-email" className="block text-xs font-medium uppercase tracking-wide text-white/60 mb-1.5">
            {t('contacts:form.email')} *
          </label>
          <input
            id="contact-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('contacts:form.emailPlaceholder')}
            className={cn(inputBase, errors.email ? inputError : inputNormal)}
          />
          {errors.email && <p className="mt-1.5 text-sm text-red-400">{errors.email}</p>}
        </div>

        {/* Message */}
        <div className="md:col-span-2">
          <label htmlFor="contact-message" className="block text-xs font-medium uppercase tracking-wide text-white/60 mb-1.5">
            {t('contacts:form.message')} *
          </label>
          <textarea
            id="contact-message"
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('contacts:form.messagePlaceholder')}
            className={cn(inputBase, errors.message ? inputError : inputNormal, 'resize-none')}
          />
          {errors.message && <p className="mt-1.5 text-sm text-red-400">{errors.message}</p>}
        </div>
      </div>

      {/* Privacy */}
      <div className="mt-6">
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={privacy}
            onChange={(e) => setPrivacy(e.target.checked)}
            className={cn(
              'mt-1 h-4 w-4 rounded border bg-ink-deep/60 text-primary focus:ring-primary',
              errors.privacy ? 'border-red-400' : 'border-white/10'
            )}
          />
          <span className="text-sm text-white/70 group-hover:text-white/90 transition-colors">
            {t('contacts:form.privacy')}{' '}
            <LocalizedLink to="privacy" className="text-primary hover:text-primary-light underline underline-offset-4">
              {t('contacts:form.privacyLink')}
            </LocalizedLink>
            .
          </span>
        </label>
        {errors.privacy && <p className="mt-1.5 text-sm text-red-400">{errors.privacy}</p>}
      </div>

      {submitError && (
        <div className="mt-6 p-4 rounded-lg bg-red-500/10 border border-red-400/20">
          <p className="text-sm text-red-300">{submitError}</p>
        </div>
      )}

      <div className="mt-8">
        <Button
          type="submit"
          disabled={submitting}
          className="w-full md:w-auto"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              {t('contacts:form.submitting')}
            </>
          ) : (
            t('contacts:form.submit')
          )}
        </Button>
      </div>
    </form>
  )
}
